import { Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  usePrintableFilevineSectionsCreateMutation,
  usePrintableFilevineSectionsDestroyMutation,
  usePrintableFilevineSectionsListQuery,
} from '@/services/api/printableFilevineSectionsService';

interface PrintableFilevineSectionsProps {
  firmId: string;
}

export const PrintableFilevineSections: React.FC<
  PrintableFilevineSectionsProps
> = ({ firmId }) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [sectionName, setSectionName] = useState('');
  const [sectionUrlComponent, setSectionUrlComponent] = useState('');

  const {
    data: sections = [],
    refetch,
    isLoading,
  } = usePrintableFilevineSectionsListQuery({ firmId });

  const [createSection, { isLoading: isCreating }] =
    usePrintableFilevineSectionsCreateMutation();
  const [deleteSection] = usePrintableFilevineSectionsDestroyMutation();

  const handleAdd = async () => {
    if (!sectionName.trim() || !sectionUrlComponent.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await createSection({
        firmId,
        createPrintableFilevineSectionSchema: {
          firm_id: firmId,
          section_name: sectionName,
          section_url_component: sectionUrlComponent,
        },
      }).unwrap();

      toast.success('Section added successfully');
      setSectionName('');
      setSectionUrlComponent('');
      setIsAddDialogOpen(false);
      refetch();
    } catch (error) {
      console.error('Error adding section:', error);
      toast.error('Failed to add section');
    }
  };

  const handleDelete = async (sectionId: string, sn: string) => {
    if (
      !window.confirm(
        `Are you sure you want to remove "${sn}" from printable sections?`,
      )
    ) {
      return;
    }

    try {
      await deleteSection({ firmId, id: sectionId }).unwrap();
      toast.success('Section removed successfully');
      refetch();
    } catch (error) {
      console.error('Error deleting section:', error);
      toast.error('Failed to remove section');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Printable Filevine Sections</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage which Filevine sections get printed for this firm
          </p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>Add Section</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Printable Section</DialogTitle>
              <DialogDescription>
                Add a new Filevine section to be included in prints
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="section-name">Section Name</Label>
                <Input
                  id="section-name"
                  placeholder="e.g., Client Information"
                  value={sectionName}
                  onChange={(e) => setSectionName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="section-url">Section URL Component</Label>
                <Input
                  id="section-url"
                  placeholder="e.g., client-information"
                  value={sectionUrlComponent}
                  onChange={(e) => setSectionUrlComponent(e.target.value)}
                />
                <p className="text-xs text-gray-500">
                  The URL component from Filevine (usually lowercase with
                  dashes)
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsAddDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleAdd} disabled={isCreating}>
                {isCreating ? 'Adding...' : 'Add Section'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border rounded-md">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left">Section Name</th>
              <th className="px-3 py-2 text-left">URL Component</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td className="px-3 py-3" colSpan={3}>
                  Loading...
                </td>
              </tr>
            ) : sections.length === 0 ? (
              <tr>
                <td className="px-3 py-3 text-center text-gray-500" colSpan={3}>
                  No printable sections configured yet. Click &quot;Add
                  Section&quot; to get started.
                </td>
              </tr>
            ) : (
              sections.map((section) => (
                <tr key={section.id} className="border-t">
                  <td className="px-3 py-2">{section.section_name}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {section.section_url_component}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        handleDelete(section.id as string, section.section_name)
                      }
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
